import { ContactDetails } from "../ContactDetails";
import { ProductDetails } from "../ProductDetails";
import { Steps } from "../Steps";
import { Checkout } from "../Checkout";
import { AssetImage, EnumProductType } from "gql/generated";
import { HeroImage } from "../HeroImage";
import { InvoiceAdminBillingProvider, useBookerContext } from "widgets/booker/contexts";
import {
  ModalContentLayout,
  StripeElementsWrapper,
} from "widgets/booker/shared";
import { ContentHeader } from "../ContentHeader";
import { ClosedBookerPage } from "../ClosedBookerPage";
import { AdminInvoicePanel } from "../AdminInvoicePanel";
import {
  CatalogCartBar,
  CatalogCheckoutPane,
  MerchCatalog,
  MerchCatalogList,
} from "../MerchCatalog";

type SingleProductModalContentProps = {
  isBookerClosed: boolean;
  catalogCheckoutOpen: boolean;
  onCatalogCheckoutOpenChange: (open: boolean) => void;
};

export const SingleProductModalContent = ({
  isBookerClosed,
  catalogCheckoutOpen,
  onCatalogCheckoutOpenChange,
}: SingleProductModalContentProps) => {
  const {
    product,
    booker,
    error,
    loading,
    isEmbedded,
    stripeTestMode,
    storefrontIsPaired,
    invoiceAdminMode,
    broadcastId,
    isCatalog,
    catalogLayout,
  } = useBookerContext();
  const connectedAccountId = booker?.paymentMetadata?.connectedAccountId ?? "";
  const canRenderCheckout = !!connectedAccountId || !!storefrontIsPaired;

  if (error && !loading) {
    throw new Error("Error loading product and/or operator data");
  }

  const featuredImage = booker?.customizations?.featuredImage;
  const doesFeaturedImageExist = !!product?.images.some(
    image => image._id === featuredImage?._id,
  );

  const updatedImagesArray = doesFeaturedImageExist
    ? [
        featuredImage,
        ...(product
          ? product.images.filter(image => image._id !== featuredImage?._id)
          : []),
      ]
    : featuredImage
      ? [featuredImage, ...(product?.images ?? [])]
      : product?.images;

  const imagesExist = !!updatedImagesArray && updatedImagesArray.length > 0;

  /** Fee, renewable fee, and merch: no accommodation-style product details block */
  const isMainProductFee =
    product?.type === EnumProductType.Fee ||
    product?.type === EnumProductType.RenewableFee ||
    product?.type === EnumProductType.Merch;

  // Show the closed booker page in live customer-facing contexts: the embedded
  // widget (isEmbedded) and Broadcast storefront resources (broadcastId). It is
  // intentionally suppressed when previewing a widget within Whereabouts and in
  // admin/invoice flows, so staff are never blocked by a closed booker.
  if (isBookerClosed && (isEmbedded || !!broadcastId)) {
    return <ClosedBookerPage />;
  }

  if (isCatalog && !invoiceAdminMode) {
    const storeImages = featuredImage ? [featuredImage] : [];
    const storeImagesExist = storeImages.length > 0;

    return (
      <>
        <HeroImage images={storeImages as AssetImage[]} />
        {canRenderCheckout && (
          <StripeElementsWrapper
            connectedAccountId={connectedAccountId}
            allowMissingAccount={storefrontIsPaired}
            stripeRegion={booker?.paymentMetadata?.stripeRegion ?? "CA"}
            stripeTestMode={stripeTestMode}
          >
            <ModalContentLayout
              imagesExist={storeImagesExist}
              header={<ContentHeader imagesExist={storeImagesExist} />}
            >
              {catalogLayout === "grid" ? <MerchCatalog /> : <MerchCatalogList />}
            </ModalContentLayout>
            <CatalogCartBar onCheckout={() => onCatalogCheckoutOpenChange(true)} />
            <CatalogCheckoutPane
              open={catalogCheckoutOpen}
              onClose={() => onCatalogCheckoutOpenChange(false)}
            />
          </StripeElementsWrapper>
        )}
      </>
    );
  }

  return (
    <>
      <HeroImage images={(updatedImagesArray ?? []) as AssetImage[]} />
      {canRenderCheckout && (
        <StripeElementsWrapper
          connectedAccountId={connectedAccountId}
          allowMissingAccount={storefrontIsPaired}
          stripeRegion={booker?.paymentMetadata?.stripeRegion ?? "CA"}
          stripeTestMode={stripeTestMode}
        >
          <InvoiceAdminBillingProvider>
            <ModalContentLayout
              imagesExist={imagesExist}
              header={<ContentHeader imagesExist={imagesExist} />}
            >
              {invoiceAdminMode ? (
                <>
                  <ContactDetails />
                  {!isMainProductFee && <ProductDetails />}
                  <Steps />
                  <AdminInvoicePanel />
                </>
              ) : (
                <>
                  <ContactDetails />
                  {!isMainProductFee && <ProductDetails />}
                  <Steps />
                  <Checkout />
                </>
              )}
            </ModalContentLayout>
          </InvoiceAdminBillingProvider>
        </StripeElementsWrapper>
      )}
    </>
  );
};
